<template>
  <section id="top" class="hero">
    <div class="hero-copy">
      <h1 v-reveal class="hero-title">
        <span class="gradient-text">Building software</span><br />
        <span class="gradient-text">from the </span><span class="future">future.</span>
      </h1>
      <p v-reveal="120" class="hero-sub">
        I'm Bisrat Tadesse, a computer science student at the University of Maryland
        building AI assistants, GPU engines, and apps people use every day.
      </p>
      <a v-reveal="220" href="#lifeos" class="chip hero-cta">
        <span class="mini-mark">B</span>
        Explore my work
      </a>
    </div>

    <div class="orbit-system" aria-hidden="true">
      <div
        v-for="(ring, i) in rings"
        :key="'ring' + i"
        class="ring"
        :style="{ width: ring * 2 + 'px', height: ring * 2 + 'px', opacity: 0.16 - i * 0.012 }"
      ></div>

      <div
        v-for="p in planets"
        :key="p.name"
        class="orbit"
        :style="{
          width: p.r * 2 + 'px',
          height: p.r * 2 + 'px',
          animationDuration: p.dur + 's',
          animationDelay: -p.dur * p.start + 's',
        }"
      >
        <a
          :href="p.href"
          class="planet"
          :title="p.name"
          tabindex="-1"
          :style="{
            width: p.size + 'px',
            height: p.size + 'px',
            background: p.fill,
            boxShadow: `0 0 ${p.size}px ${p.glow}`,
            animationDuration: p.dur + 's',
            animationDelay: -p.dur * p.start + 's',
          }"
        >
          <font-awesome-icon v-if="p.icon" :icon="p.icon" class="planet-icon" />
        </a>
      </div>

      <div class="core">
        <div class="core-glow"></div>
        <div class="core-mark">B</div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'HeroOrbit',
  data() {
    return {
      rings: [150, 220, 290, 360, 430, 500, 570, 640, 710, 780],
      planets: [
        {
          name: 'LifeOS', href: '#lifeos', r: 290, size: 46, dur: 70, start: 0.12,
          fill: 'radial-gradient(circle at 32% 28%, #b7f7dc, #34d399 45%, #065f46)',
          glow: 'rgba(52,211,153,0.35)', icon: ['fas', 'calendar-check'],
        },
        {
          name: 'Quant engine', href: '#quant', r: 430, size: 58, dur: 95, start: 0.82,
          fill: 'radial-gradient(circle at 32% 28%, #fde68a, #f59e0b 45%, #9a3412)',
          glow: 'rgba(245,158,11,0.35)', icon: ['fas', 'microchip'],
        },
        {
          name: 'Jarvis', href: '#jarvis', r: 220, size: 64, dur: 55, start: 0.45,
          fill: 'radial-gradient(circle at 32% 28%, #ddd6fe, #8b5cf6 45%, #3b0764)',
          glow: 'rgba(139,92,246,0.45)', icon: ['fas', 'microphone-lines'],
        },
        {
          name: 'AgendaVue', href: '#agendavue', r: 570, size: 52, dur: 120, start: 0.62,
          fill: 'radial-gradient(circle at 32% 28%, #bfdbfe, #3b82f6 45%, #1e3a8a)',
          glow: 'rgba(59,130,246,0.35)', icon: ['fas', 'graduation-cap'],
        },
        {
          name: 'Moon', href: '#top', r: 360, size: 18, dur: 80, start: 0.95,
          fill: 'radial-gradient(circle at 32% 28%, #fecaca, #ef4444 50%, #7f1d1d)',
          glow: 'rgba(239,68,68,0.3)',
        },
        {
          name: 'Ice', href: '#top', r: 500, size: 24, dur: 105, start: 0.3,
          fill: 'radial-gradient(circle at 32% 28%, #e0f2fe, #7dd3fc 50%, #0e7490)',
          glow: 'rgba(125,211,252,0.3)',
        },
        {
          name: 'Dust', href: '#top', r: 710, size: 72, dur: 160, start: 0.08,
          fill: 'radial-gradient(circle at 32% 28%, #6b6b72, #2c2c31 55%, #111113)',
          glow: 'rgba(255,255,255,0.06)',
        },
      ],
    };
  },
};
</script>

<style scoped>
.hero {
  position: relative;
  height: 1640px;
  overflow: hidden;
  z-index: 1;
}

.hero-copy {
  position: relative;
  z-index: 5;
  padding: 190px 24px 0;
  text-align: center;
}

.hero-title {
  font-size: clamp(48px, 8.4vw, 112px);
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.035em;
}

.future {
  background: linear-gradient(90deg, #ffffff 0%, #c4b5fd 45%, #6b6b75 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.hero-sub {
  max-width: 560px;
  margin: 28px auto 0;
  font-size: clamp(17px, 1.6vw, 20px);
  line-height: 1.5;
  color: var(--text-dim);
}

.hero-cta {
  margin-top: 36px;
  padding: 10px 20px 10px 10px;
  background: rgba(40, 40, 44, 0.8);
  backdrop-filter: blur(10px);
}

.mini-mark {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--text);
  color: var(--bg);
  font-size: 12px;
  font-weight: 700;
}

.orbit-system {
  position: absolute;
  left: 50%;
  top: 1020px;
  width: 0;
  height: 0;
}

.ring,
.orbit {
  position: absolute;
  left: 0;
  top: 0;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.ring {
  border: 1px solid #ffffff;
}

.orbit {
  animation: spin linear infinite;
}

.planet {
  position: absolute;
  left: 50%;
  top: 0;
  display: grid;
  place-items: center;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  animation: counter linear infinite;
  pointer-events: auto;
}

.planet-icon {
  color: rgba(255, 255, 255, 0.85);
  width: 40%;
  height: 40%;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
}

@keyframes spin {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

@keyframes counter {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(-360deg); }
}

.core {
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -50%);
}

.core-glow {
  position: absolute;
  inset: -80px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(196, 181, 253, 0.18), transparent 65%);
}

.core-mark {
  position: relative;
  display: grid;
  place-items: center;
  width: 210px;
  height: 210px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 28%, #ffffff, #e9e9ee 45%, #a3a3ad 100%);
  box-shadow:
    inset -14px -18px 30px rgba(0, 0, 0, 0.25),
    inset 10px 12px 24px rgba(255, 255, 255, 0.9),
    0 30px 80px rgba(0, 0, 0, 0.6),
    0 0 0 10px rgba(255, 255, 255, 0.03);
  color: #141417;
  font-size: 128px;
  font-weight: 700;
  letter-spacing: -0.04em;
  line-height: 1;
  padding-bottom: 6px;
}

@media (max-width: 760px) {
  .hero { height: 1180px; }
  .hero-copy { padding-top: 130px; }
  .orbit-system { top: 760px; transform: scale(0.62); }
}
</style>
